import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  getLibrary,
  updateLibrary,
  searchGroups,
  addGroupsToLibrary,
  removeGroupsFromLibrary
} from './core/_requests'

interface Group {
  _id: string
  name: string
  group_url: string
  description?: string
  location?: string
  follow_count?: number
  category?: { _id: string; name: string }[]
}

interface Library {
  _id: string
  name: string
  is_public: boolean
  facebook_groups: Group[]
  categories?: { _id: string; name: string }[]
  account?: { first_name?: string; last_name?: string; email?: string }
  created_at: string
  assigned_campaigns?: { _id: string; name: string }[]
  search_keywords?: string[]
  min_followers?: number
  excluded_groups?: string[]
  status?: string
}

const LibraryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [library, setLibrary] = useState<Library | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Edit state
  const [editName, setEditName] = useState('')
  const [editPublic, setEditPublic] = useState(false)
  const [isEditingName, setIsEditingName] = useState(false)

  // Search state with tags
  const [searchTags, setSearchTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [minFollowers, setMinFollowers] = useState(2000) // Default 2000
  const [searchResults, setSearchResults] = useState<Group[]>([])
  const [searching, setSearching] = useState(false)
  const [selectedToAdd, setSelectedToAdd] = useState<Set<string>>(new Set())
  const [excludedGroups, setExcludedGroups] = useState<Set<string>>(new Set())
  const [searchPage, setSearchPage] = useState(1)
  const [searchTotalPages, setSearchTotalPages] = useState(1)
  const [totalResults, setTotalResults] = useState(0)

  // Selected library groups for removal
  const [selectedToRemove, setSelectedToRemove] = useState<Set<string>>(new Set())
  const [librarySearchQuery, setLibrarySearchQuery] = useState('')

  // View mode
  const [viewMode, setViewMode] = useState<'search' | 'library' | 'blacklist'>('search')

  useEffect(() => {
    loadLibrary()
  }, [id])

  // Auto search when tags or minFollowers change
  useEffect(() => {
    if (searchTags.length > 0) {
      handleSearch(1)
    } else {
      setSearchResults([])
      setTotalResults(0)
    }
  }, [searchTags, minFollowers])

  // Save search settings when they change
  useEffect(() => {
    if (!id || loading) return
    const saveSearchSettings = async () => {
      try {
        await updateLibrary(id, {
          search_keywords: searchTags,
          min_followers: minFollowers,
          excluded_groups: Array.from(excludedGroups)
        })
      } catch (error) {
        console.error('Failed to save search settings:', error)
      }
    }
    // Debounce the save
    const timer = setTimeout(saveSearchSettings, 500)
    return () => clearTimeout(timer)
  }, [searchTags, minFollowers, excludedGroups, id, loading])

  const loadLibrary = async () => {
    if (!id) return
    setLoading(true)
    try {
      const res = await getLibrary(id)
      setLibrary(res.library)
      setEditName(res.library.name)
      setEditPublic(res.library.is_public)
      // Load saved search settings
      if (res.library.search_keywords?.length > 0) {
        setSearchTags(res.library.search_keywords)
      }
      // Use saved min_followers or default to 2000
      setMinFollowers(res.library.min_followers ?? 2000)
      // Load excluded groups
      if (res.library.excluded_groups?.length > 0) {
        setExcludedGroups(new Set(res.library.excluded_groups))
      }
    } catch (error) {
      console.error('Failed to load library:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!id || !editName.trim()) return
    setSaving(true)
    try {
      await updateLibrary(id, {
        name: editName.trim(),
        is_public: editPublic
      })
      loadLibrary()
    } catch (error) {
      alert('Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  const handleSearch = async (page = 1) => {
    if (searchTags.length === 0) return
    setSearching(true)
    setSearchPage(page)
    try {
      const res = await searchGroups({
        keywords: searchTags.join(','), // Join with comma - each tag is OR, words within tag are AND
        minFollowers: minFollowers > 0 ? minFollowers : undefined,
        page,
        limit: 50
      })
      setSearchResults(res.groups)
      setSearchTotalPages(res.pagination.pages)
      setTotalResults(res.pagination.total)
    } catch (error) {
      console.error('Failed to search groups:', error)
    } finally {
      setSearching(false)
    }
  }

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault()
      const newTag = tagInput.trim().replace(/,/g, '')
      if (newTag && !searchTags.includes(newTag)) {
        setSearchTags([...searchTags, newTag])
      }
      setTagInput('')
    }
  }

  const removeTag = (tagToRemove: string) => {
    setSearchTags(searchTags.filter(tag => tag !== tagToRemove))
  }

  const handleAddGroups = async () => {
    if (!id || selectedToAdd.size === 0) return
    try {
      await addGroupsToLibrary(id, Array.from(selectedToAdd))
      setSelectedToAdd(new Set())
      loadLibrary()
    } catch (error) {
      alert('Failed to add groups')
    }
  }

  const handleRemoveGroups = async () => {
    if (!id || selectedToRemove.size === 0) return
    if (!window.confirm(`האם אתה בטוח שברצונך להסיר ${selectedToRemove.size} קבוצות מהספרייה?`)) return
    try {
      await removeGroupsFromLibrary(id, Array.from(selectedToRemove))
      setSelectedToRemove(new Set())
      loadLibrary()
    } catch (error) {
      alert('Failed to remove groups')
    }
  }

  const toggleAddSelection = (groupId: string) => {
    setSelectedToAdd(prev => {
      const newSet = new Set(prev)
      if (newSet.has(groupId)) {
        newSet.delete(groupId)
      } else {
        newSet.add(groupId)
      }
      return newSet
    })
  }

  const toggleRemoveSelection = (groupId: string) => {
    setSelectedToRemove(prev => {
      const newSet = new Set(prev)
      if (newSet.has(groupId)) {
        newSet.delete(groupId)
      } else {
        newSet.add(groupId)
      }
      return newSet
    })
  }

  const selectAllResults = () => {
    const newSet = new Set(selectedToAdd)
    filteredSearchResults.forEach(g => newSet.add(g._id))
    setSelectedToAdd(newSet)
  }

  const toggleExcludeGroup = (groupId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setExcludedGroups(prev => {
      const newSet = new Set(prev)
      if (newSet.has(groupId)) {
        newSet.delete(groupId)
      } else {
        newSet.add(groupId)
      }
      return newSet
    })
  }

  const handleNameSave = async () => {
    if (!id || !editName.trim() || editName.trim() === library?.name) {
      setEditName(library?.name || '')
      setIsEditingName(false)
      return
    }
    try {
      await updateLibrary(id, { name: editName.trim() })
      loadLibrary()
      setIsEditingName(false)
    } catch (error) {
      console.error('Failed to update name:', error)
    }
  }

  const handleStatusChange = async (newStatus: string) => {
    if (!id) return
    try {
      await updateLibrary(id, { status: newStatus })
      loadLibrary()
    } catch (error) {
      console.error('Failed to update status:', error)
    }
  }

  // Calculate combined followers for selected groups
  const getSelectedFollowersCount = () => {
    let total = 0
    selectedToAdd.forEach(groupId => {
      const group = searchResults.find(g => g._id === groupId)
      if (group?.follow_count) total += group.follow_count
    })
    return total
  }

  // Get excluded groups details
  const getExcludedGroupsDetails = () => {
    return searchResults.filter(g => excludedGroups.has(g._id))
  }

  if (loading) {
    return (
      <div style={{ padding: '80px', textAlign: 'center', direction: 'rtl' }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '3px solid #e0e0e0',
          borderTopColor: '#667eea',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          margin: '0 auto 20px'
        }}></div>
        <div style={{ color: '#666' }}>טוען ספרייה...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  if (!library) {
    return (
      <div style={{ padding: '80px', textAlign: 'center', direction: 'rtl' }}>
        <i className="bi bi-exclamation-triangle" style={{ fontSize: '48px', color: '#ff6b6b' }}></i>
        <div style={{ color: '#1a1a2e', fontSize: '18px', marginTop: '16px' }}>ספרייה לא נמצאה</div>
        <button
          onClick={() => navigate('/admin/libraries')}
          style={{
            marginTop: '24px',
            padding: '12px 24px',
            borderRadius: '10px',
            border: 'none',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          חזרה לספריות
        </button>
      </div>
    )
  }

  // Filter out already added groups from search results
  const existingGroupIds = new Set(library.facebook_groups.map(g => g._id))
  const filteredSearchResults = searchResults.filter(g => !existingGroupIds.has(g._id))

  // Filter library groups by search query
  const filteredLibraryGroups = library.facebook_groups.filter(g =>
    librarySearchQuery ? g.name.toLowerCase().includes(librarySearchQuery.toLowerCase()) : true
  )

  return (
    <div style={{ padding: 0, direction: 'rtl' }}>
      {/* Back Button & Header */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/admin/libraries')}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: '1px solid #ddd',
            background: 'transparent',
            color: '#666',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px',
          }}
        >
          <i className="bi bi-arrow-right"></i>
          חזרה לספריות
        </button>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 700,
            color: '#1a1a2e',
            margin: 0,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            בחירת קבוצות לספרייה:{' '}
            {isEditingName ? (
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onBlur={handleNameSave}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleNameSave()
                  if (e.key === 'Escape') { setEditName(library.name); setIsEditingName(false) }
                }}
                autoFocus
                style={{
                  fontSize: '28px',
                  fontWeight: 700,
                  border: 'none',
                  borderBottom: '2px solid #667eea',
                  outline: 'none',
                  background: 'transparent',
                  color: '#1a1a2e',
                  padding: '0 4px',
                  minWidth: '100px',
                  WebkitTextFillColor: '#1a1a2e'
                }}
              />
            ) : (
              <span
                onClick={() => setIsEditingName(true)}
                style={{ cursor: 'pointer' }}
                title="לחץ לעריכת שם הספרייה"
              >
                {library.name}
                <i className="bi bi-pencil" style={{
                  fontSize: '16px',
                  marginRight: '6px',
                  opacity: 0.5,
                  WebkitTextFillColor: '#667eea'
                }}></i>
              </span>
            )}
          </h1>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              padding: '8px 16px',
              background: 'rgba(240, 147, 251, 0.1)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="bi bi-collection" style={{ color: '#f093fb' }}></i>
              <span style={{ color: '#1a1a2e', fontWeight: 600 }}>{library.facebook_groups.length}</span>
              <span style={{ color: '#666', fontSize: '13px' }}>קבוצות בספרייה</span>
            </div>
            <div style={{
              padding: '8px 16px',
              background: 'rgba(102, 126, 234, 0.1)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="bi bi-megaphone" style={{ color: '#667eea' }}></i>
              <span style={{ color: '#1a1a2e', fontWeight: 600 }}>{library.assigned_campaigns?.length || 0}</span>
              <span style={{ color: '#666', fontSize: '13px' }}>קמפיינים</span>
            </div>
            <select
              value={library.status || 'draft'}
              onChange={(e) => handleStatusChange(e.target.value)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '2px solid #ddd',
                background: '#f5f5f5',
                color: '#1a1a2e',
                fontSize: '13px',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              <option value="draft">טיוטה</option>
              <option value="in_progress">בעבודה</option>
              <option value="needs_review">לבדיקה</option>
              <option value="finished">מוכן</option>
              <option value="archived">ארכיון</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        background: 'linear-gradient(180deg, #f5f5f5 0%, #fafafa 100%)',
        borderRadius: '20px',
        border: '1px solid #ddd',
        overflow: 'hidden',
      }}>
        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #ddd',
        }}>
          <button
            onClick={() => setViewMode('search')}
            style={{
              flex: 1,
              padding: '16px 24px',
              border: 'none',
              background: viewMode === 'search' ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
              color: viewMode === 'search' ? '#667eea' : '#666',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              borderBottom: viewMode === 'search' ? '2px solid #667eea' : '2px solid transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <i className="bi bi-search"></i>
            חיפוש והוספת קבוצות
            {selectedToAdd.size > 0 && (
              <span style={{
                background: '#667eea',
                color: '#fff',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '11px',
              }}>
                {selectedToAdd.size}
              </span>
            )}
          </button>
          <button
            onClick={() => setViewMode('library')}
            style={{
              flex: 1,
              padding: '16px 24px',
              border: 'none',
              background: viewMode === 'library' ? 'rgba(240, 147, 251, 0.1)' : 'transparent',
              color: viewMode === 'library' ? '#f093fb' : '#666',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              borderBottom: viewMode === 'library' ? '2px solid #f093fb' : '2px solid transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <i className="bi bi-collection"></i>
            קבוצות בספרייה ({library.facebook_groups.length})
          </button>
          <button
            onClick={() => setViewMode('blacklist')}
            style={{
              flex: 1,
              padding: '16px 24px',
              border: 'none',
              background: viewMode === 'blacklist' ? 'rgba(255, 107, 107, 0.1)' : 'transparent',
              color: viewMode === 'blacklist' ? '#ff6b6b' : '#666',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              borderBottom: viewMode === 'blacklist' ? '2px solid #ff6b6b' : '2px solid transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <i className="bi bi-x-circle"></i>
            רשימה שחורה ({excludedGroups.size})
          </button>
        </div>

        {/* Search Tab */}
        {viewMode === 'search' && (
          <div style={{ padding: '24px' }}>
            {/* Tag Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#1a1a2e', fontSize: '14px', fontWeight: 600, display: 'block', marginBottom: '12px' }}>
                <i className="bi bi-tags me-2"></i>
                מילות מפתח לחיפוש
              </label>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                padding: '12px 16px',
                background: '#f5f5f5',
                border: '2px solid #ddd',
                borderRadius: '12px',
                minHeight: '52px',
                alignItems: 'center',
              }}>
                {searchTags.map((tag, index) => (
                  <span
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#fff',
                    }}
                  >
                    {tag}
                    <i
                      className="bi bi-x"
                      onClick={() => removeTag(tag)}
                      style={{ cursor: 'pointer', fontSize: '14px' }}
                    ></i>
                  </span>
                ))}
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleAddTag}
                  placeholder={searchTags.length === 0 ? "הקלד מילת מפתח ולחץ Enter..." : "הוסף עוד..."}
                  style={{
                    flex: 1,
                    minWidth: '150px',
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: '#1a1a2e',
                    fontSize: '14px',
                  }}
                />
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                לדוגמה: נדל"ן, תל אביב, השקעות
              </div>
            </div>

            {/* Min Followers Filter */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#1a1a2e', fontSize: '14px', fontWeight: 600, display: 'block', marginBottom: '12px' }}>
                <i className="bi bi-people me-2"></i>
                מינימום עוקבים
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="number"
                  value={minFollowers || ''}
                  onChange={(e) => setMinFollowers(parseInt(e.target.value) || 0)}
                  placeholder="0"
                  style={{
                    width: '150px',
                    padding: '12px 16px',
                    background: '#f5f5f5',
                    border: '2px solid #ddd',
                    borderRadius: '10px',
                    color: '#1a1a2e',
                    fontSize: '14px',
                    outline: 'none',
                  }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[1000, 5000, 10000, 50000].map(num => (
                    <button
                      key={num}
                      onClick={() => setMinFollowers(num)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: minFollowers === num ? '2px solid #667eea' : '2px solid #ddd',
                        background: minFollowers === num ? 'rgba(102, 126, 234, 0.2)' : '#f5f5f5',
                        color: minFollowers === num ? '#667eea' : '#666',
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      {num >= 1000 ? `${num / 1000}K` : num}
                    </button>
                  ))}
                  {minFollowers > 0 && (
                    <button
                      onClick={() => setMinFollowers(0)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '2px solid rgba(255, 107, 107, 0.3)',
                        background: 'rgba(255, 107, 107, 0.1)',
                        color: '#ff6b6b',
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      איפוס
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Selection Counter - like client side */}
            {searchTags.length > 0 && (
              <div style={{
                background: 'rgba(102, 126, 234, 0.1)',
                borderRadius: '12px',
                padding: '16px 20px',
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      padding: '6px 14px',
                      borderRadius: '8px',
                      fontSize: '18px',
                      fontWeight: 700,
                    }}>
                      {selectedToAdd.size}
                    </span>
                    <span style={{ color: '#1a1a2e', fontSize: '14px', fontWeight: 500 }}>קבוצות נבחרו</span>
                    {selectedToAdd.size > 0 && (
                      <span style={{ color: '#11998e', fontSize: '13px', marginRight: '8px' }}>
                        <i className="bi bi-people-fill me-1"></i>
                        {getSelectedFollowersCount().toLocaleString()} עוקבים
                      </span>
                    )}
                  </div>
                  <div style={{ color: '#666', fontSize: '14px' }}>
                    {searching ? (
                      <span><i className="bi bi-hourglass-split me-2"></i>מחפש...</span>
                    ) : (
                      <span>מתוך <strong style={{ color: '#1a1a2e' }}>{totalResults.toLocaleString()}</strong> קבוצות זמינות</span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  {filteredSearchResults.length > 0 && (
                    <button
                      onClick={selectAllResults}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1px solid #bbb',
                        background: 'transparent',
                        color: '#1a1a2e',
                        fontSize: '13px',
                        cursor: 'pointer',
                      }}
                    >
                      בחר הכל בעמוד
                    </button>
                  )}
                  {selectedToAdd.size > 0 && (
                    <button
                      onClick={handleAddGroups}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                        color: '#fff',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <i className="bi bi-plus-lg"></i>
                      הוסף {selectedToAdd.size} קבוצות לספרייה
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Search Results */}
            {searchTags.length === 0 ? (
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <i className="bi bi-search" style={{ fontSize: '48px', color: '#666', display: 'block', marginBottom: '16px' }}></i>
                <div style={{ color: '#1a1a2e', fontSize: '16px', fontWeight: 500 }}>חפש קבוצות להוספה</div>
                <div style={{ color: '#666', fontSize: '14px', marginTop: '8px' }}>הוסף מילות מפתח כדי לחפש קבוצות רלוונטיות</div>
              </div>
            ) : filteredSearchResults.length === 0 && !searching ? (
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <i className="bi bi-emoji-frown" style={{ fontSize: '48px', color: '#666', display: 'block', marginBottom: '16px' }}></i>
                <div style={{ color: '#1a1a2e', fontSize: '16px', fontWeight: 500 }}>לא נמצאו קבוצות חדשות</div>
                <div style={{ color: '#666', fontSize: '14px', marginTop: '8px' }}>נסה מילות מפתח אחרות</div>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                gap: '12px',
              }}>
                {filteredSearchResults.filter(g => !excludedGroups.has(g._id)).map((group) => (
                  <div
                    key={group._id}
                    onClick={() => toggleAddSelection(group._id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '14px 16px',
                      background: selectedToAdd.has(group._id) ? 'rgba(102, 126, 234, 0.15)' : '#f8f9fa',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      border: selectedToAdd.has(group._id) ? '2px solid #667eea' : '2px solid #eee',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '6px',
                      border: `2px solid ${selectedToAdd.has(group._id) ? '#667eea' : '#bbb'}`,
                      background: selectedToAdd.has(group._id) ? '#667eea' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {selectedToAdd.has(group._id) && (
                        <i className="bi bi-check" style={{ color: '#fff', fontSize: '14px' }}></i>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, color: '#1a1a2e', fontSize: '14px' }}>
                        {group.name}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
                        {group.location && (
                          <span style={{ fontSize: '12px', color: '#666' }}>
                            <i className="bi bi-geo-alt me-1"></i>{group.location}
                          </span>
                        )}
                        <span style={{ fontSize: '12px', color: '#555' }}>
                          <i className="bi bi-people me-1"></i>{group.follow_count?.toLocaleString() || 0}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => toggleExcludeGroup(group._id, e)}
                      title="הוסף לרשימה שחורה"
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'rgba(255, 107, 107, 0.1)',
                        color: '#ff6b6b',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <i className="bi bi-x-circle"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {searchTotalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
                <button
                  onClick={() => handleSearch(searchPage - 1)}
                  disabled={searchPage === 1}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: searchPage === 1 ? '#f8f9fa' : '#eee',
                    color: searchPage === 1 ? '#aaa' : '#333',
                    cursor: searchPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                  }}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <span style={{ color: '#555', fontSize: '13px', lineHeight: '36px', margin: '0 12px' }}>
                  עמוד {searchPage} מתוך {searchTotalPages}
                </span>
                <button
                  onClick={() => handleSearch(searchPage + 1)}
                  disabled={searchPage === searchTotalPages}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: searchPage === searchTotalPages ? '#f8f9fa' : '#eee',
                    color: searchPage === searchTotalPages ? '#aaa' : '#333',
                    cursor: searchPage === searchTotalPages ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                  }}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Library Groups Tab */}
        {viewMode === 'library' && (
          <div>
            {/* Search Input */}
            {library.facebook_groups.length > 0 && (
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #eee' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  background: '#f5f5f5',
                  border: '2px solid #ddd',
                  borderRadius: '12px',
                }}>
                  <i className="bi bi-search" style={{ color: '#999' }}></i>
                  <input
                    type="text"
                    value={librarySearchQuery}
                    onChange={(e) => setLibrarySearchQuery(e.target.value)}
                    placeholder="חפש קבוצות בספרייה..."
                    style={{
                      flex: 1,
                      background: 'transparent',
                      border: 'none',
                      outline: 'none',
                      color: '#1a1a2e',
                      fontSize: '14px',
                    }}
                  />
                  {librarySearchQuery && (
                    <button
                      onClick={() => setLibrarySearchQuery('')}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#999',
                        cursor: 'pointer',
                        padding: '4px',
                      }}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Actions Header */}
            {library.facebook_groups.length > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 24px',
                borderBottom: '1px solid #eee',
              }}>
                <div
                  onClick={() => {
                    const filteredIds = filteredLibraryGroups.map(g => g._id)
                    const allFilteredSelected = filteredIds.every(id => selectedToRemove.has(id))
                    if (allFilteredSelected && filteredIds.length > 0) {
                      const newSet = new Set(selectedToRemove)
                      filteredIds.forEach(id => newSet.delete(id))
                      setSelectedToRemove(newSet)
                    } else {
                      const newSet = new Set(selectedToRemove)
                      filteredIds.forEach(id => newSet.add(id))
                      setSelectedToRemove(newSet)
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '6px',
                    border: `2px solid ${filteredLibraryGroups.length > 0 && filteredLibraryGroups.every(g => selectedToRemove.has(g._id)) ? '#ff6b6b' : '#bbb'}`,
                    background: filteredLibraryGroups.length > 0 && filteredLibraryGroups.every(g => selectedToRemove.has(g._id)) ? '#ff6b6b' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {filteredLibraryGroups.length > 0 && filteredLibraryGroups.every(g => selectedToRemove.has(g._id)) && (
                      <i className="bi bi-check" style={{ color: '#fff', fontSize: '12px' }}></i>
                    )}
                  </div>
                  <span style={{ color: '#666', fontSize: '13px' }}>
                    {selectedToRemove.size > 0 ? `${selectedToRemove.size} נבחרו` : (librarySearchQuery ? 'בחר הכל בתוצאות' : 'בחר הכל')}
                  </span>
                </div>

                {selectedToRemove.size > 0 && (
                  <button
                    onClick={handleRemoveGroups}
                    style={{
                      padding: '8px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'rgba(255, 107, 107, 0.15)',
                      color: '#ff6b6b',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <i className="bi bi-trash"></i>
                    הסר {selectedToRemove.size} קבוצות
                  </button>
                )}
              </div>
            )}

            {/* Groups List */}
            {library.facebook_groups.length === 0 ? (
              <div style={{ padding: '80px', textAlign: 'center' }}>
                <i className="bi bi-collection" style={{ fontSize: '48px', color: '#666', display: 'block', marginBottom: '16px' }}></i>
                <div style={{ color: '#1a1a2e', fontSize: '16px', fontWeight: 500 }}>הספרייה ריקה</div>
                <div style={{ color: '#666', fontSize: '14px', marginTop: '8px' }}>עבור ללשונית "חיפוש והוספת קבוצות" כדי להוסיף קבוצות</div>
                <button
                  onClick={() => setViewMode('search')}
                  style={{
                    marginTop: '20px',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <i className="bi bi-search me-2"></i>
                  חפש קבוצות
                </button>
              </div>
            ) : (
              <div style={{ padding: '12px' }}>
                {librarySearchQuery && (
                  <div style={{ padding: '0 12px 12px', color: '#666', fontSize: '13px' }}>
                    נמצאו {filteredLibraryGroups.length} מתוך {library.facebook_groups.length} קבוצות
                  </div>
                )}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '12px',
                }}>
                  {filteredLibraryGroups.map((group) => (
                    <div
                      key={group._id}
                      onClick={() => toggleRemoveSelection(group._id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '14px 16px',
                        background: selectedToRemove.has(group._id) ? 'rgba(255, 107, 107, 0.1)' : '#f8f9fa',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        border: selectedToRemove.has(group._id) ? '2px solid #ff6b6b' : '2px solid #eee',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '6px',
                        border: `2px solid ${selectedToRemove.has(group._id) ? '#ff6b6b' : '#bbb'}`,
                        background: selectedToRemove.has(group._id) ? '#ff6b6b' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        {selectedToRemove.has(group._id) && (
                          <i className="bi bi-check" style={{ color: '#fff', fontSize: '14px' }}></i>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, color: '#1a1a2e', fontSize: '14px' }}>
                          {group.name}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
                          {group.location && (
                            <span style={{ fontSize: '12px', color: '#666' }}>
                              <i className="bi bi-geo-alt me-1"></i>{group.location}
                            </span>
                          )}
                          <span style={{ fontSize: '12px', color: '#555' }}>
                            <i className="bi bi-people me-1"></i>{group.follow_count?.toLocaleString() || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Blacklist Tab */}
        {viewMode === 'blacklist' && (
          <div style={{ padding: '24px' }}>
            {excludedGroups.size === 0 ? (
              <div style={{ padding: '80px', textAlign: 'center' }}>
                <i className="bi bi-x-circle" style={{ fontSize: '48px', color: '#666', display: 'block', marginBottom: '16px' }}></i>
                <div style={{ color: '#1a1a2e', fontSize: '16px', fontWeight: 500 }}>אין קבוצות ברשימה השחורה</div>
                <div style={{ color: '#666', fontSize: '14px', marginTop: '8px' }}>בזמן חיפוש, לחץ על ה-X כדי להוסיף קבוצות שאינן רלוונטיות</div>
              </div>
            ) : (
              <>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  padding: '12px 16px',
                  background: 'rgba(255, 107, 107, 0.1)',
                  borderRadius: '10px',
                }}>
                  <span style={{ color: '#ff6b6b', fontSize: '14px' }}>
                    <i className="bi bi-info-circle me-2"></i>
                    קבוצות אלו לא יופיעו בתוצאות החיפוש
                  </span>
                  <button
                    onClick={() => setExcludedGroups(new Set())}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: '1px solid #bbb',
                      background: 'transparent',
                      color: '#1a1a2e',
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    נקה הכל
                  </button>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '12px',
                }}>
                  {Array.from(excludedGroups).map((groupId) => {
                    const group = searchResults.find(g => g._id === groupId)
                    return (
                      <div
                        key={groupId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '14px 16px',
                          background: 'rgba(255, 107, 107, 0.05)',
                          borderRadius: '12px',
                          border: '2px solid rgba(255, 107, 107, 0.2)',
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, color: '#1a1a2e', fontSize: '14px' }}>
                            {group?.name || groupId}
                          </div>
                          {group && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
                              {group.location && (
                                <span style={{ fontSize: '12px', color: '#666' }}>
                                  <i className="bi bi-geo-alt me-1"></i>{group.location}
                                </span>
                              )}
                              <span style={{ fontSize: '12px', color: '#555' }}>
                                <i className="bi bi-people me-1"></i>{group.follow_count?.toLocaleString() || 0}
                              </span>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={(e) => toggleExcludeGroup(groupId, e)}
                          title="הסר מרשימה שחורה"
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            border: 'none',
                            background: 'rgba(17, 153, 142, 0.1)',
                            color: '#11998e',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <i className="bi bi-arrow-counterclockwise"></i>
                        </button>
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        select option {
          background: #fff;
          color: #333;
        }
      `}</style>
    </div>
  )
}

export default LibraryDetailPage
